const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let refreshPromise = null;

// P-AUTH (plan §2.1) — JWT auth is now cookie-based. The HttpOnly access
// and refresh cookies are set by the backend on login and ride along
// automatically with every same-origin request via credentials:'include'.
// JS cannot read them, which is the whole point — XSS payloads can no
// longer exfiltrate the session.
//
// The four token-helper exports below are retained as no-ops so legacy
// imports (api/index.js barrel; older modules that haven't been migrated
// to the cookie path yet) keep resolving. They are slated for removal in
// a follow-up cleanup pass once every consumer is verified to be cookie-
// only. Do NOT add new callers — read auth state from AuthContext / the
// /auth/me endpoint instead.

function getAccessToken() {
  return null;  // no client-readable token in cookie auth
}

function storeTokens(_accessToken, _refreshToken) {
  // No-op. Cookies are set server-side via Set-Cookie on /auth/login
  // and /auth/refresh; the frontend never persists tokens.
}

function clearAuth() {
  // Best-effort cleanup of legacy localStorage residue from versions of
  // the app that did persist tokens. Safe to leave in even after every
  // user has migrated; idempotent and removeItem on an absent key is a
  // no-op.
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  } catch {
    /* ignore — Safari ITP / private mode can throw on localStorage */
  }
}

// Read the CSRF cookie that flask-jwt-extended sets alongside the JWT
// cookie. We echo it as X-CSRF-Token on every state-changing request,
// completing the double-submit pattern. Two cookie names because the
// library issues separate access / refresh CSRF tokens.
function readCsrfCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

const ERROR_MESSAGES = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  409: 'Conflict',
  422: 'Validation Error',
  500: 'Internal Server Error',
};

async function parseErrorResponse(response) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    // response has no JSON body
  }

  const status = response.status;
  const message = body?.error || body?.message || ERROR_MESSAGES[status] || `HTTP Error ${status}`;
  const details = body?.details || body?.errors || null;
  const code = body?.code || null;

  const err = new Error(message);
  err.status = status;
  err.code = code;
  err.details = details;
  // Axios-shape compatibility: a meaningful number of PDPL components still
  // read `err.response.data.{code,error,details}` (useStepUpAuth,
  // DualSignatureCeremony, CoSignatureReview, evidenceHooks). Expose the same
  // fields under the legacy shape so those components continue to discriminate
  // step_up_failed vs step_up_locked_out vs generic 401s without needing a
  // callsite-wide refactor.
  err.response = { status, data: body || { error: message, code, details } };
  return err;
}

// 401 codes that mean "step-up auth rejected this specific request" rather
// than "JWT is expired / invalid". For these, refreshing the JWT + retrying
// is actively harmful — it doubles every failed attempt against the 5-try
// step-up lockout window.
const STEP_UP_401_CODES = new Set([
  'step_up_failed',
  'step_up_required',
  'user_inactive',
]);

async function isStepUp401(response) {
  if (response.status !== 401) return false;
  try {
    const clone = response.clone();
    const body = await clone.json();
    return STEP_UP_401_CODES.has(body?.code);
  } catch {
    return false;
  }
}

// Expose response headers to callers as a plain object with lowercased keys
// (matching fetch's own casing) so blob downloaders can read integrity headers
// like `x-privacyguard-payload-hash` via bracket access. Additive: existing
// callers that only read `.data`/`.status` are unaffected.
function headersToObject(headers) {
  if (!headers || typeof headers.entries !== 'function') return {};
  return Object.fromEntries(headers.entries());
}

function buildUrl(url, params) {
  let fullUrl;
  try {
    fullUrl = new URL(`${API_BASE}${url}`);
  } catch {
    // API_BASE is a relative path — resolve against current origin
    fullUrl = new URL(`${API_BASE}${url}`, window.location.origin);
  }

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        fullUrl.searchParams.append(key, value);
      }
    });
  }
  return fullUrl.toString();
}

async function attemptRefresh() {
  // P-AUTH — refresh runs entirely on cookies. The browser sends
  // refresh_token_cookie + csrf_refresh_token automatically; we echo
  // the CSRF cookie as X-CSRF-Refresh-Token to satisfy the
  // double-submit check. The new access + refresh cookies come back via
  // Set-Cookie on the response.
  const refreshCsrf = readCsrfCookie('csrf_refresh_token');
  const refreshHeaders = {
    'Content-Type': 'application/json',
  };
  if (refreshCsrf) {
    refreshHeaders['X-CSRF-Refresh-Token'] = refreshCsrf;
  }
  const response = await fetch(buildUrl('/auth/refresh'), {
    method: 'POST',
    headers: refreshHeaders,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Refresh failed');
  }

  // The refresh response body is no longer load-bearing — the cookies
  // are what authenticates the next request. We still consume the body
  // so the caller's promise resolves with a stable value.
  await response.json().catch(() => null);
  return true;
}

async function refreshWithMutex() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = attemptRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function request(method, url, data, options = {}) {
  const fullUrl = buildUrl(url, options.params);

  const headers = {};

  // ``options.token`` is the one remaining header path — pre-2FA tokens
  // returned in the login JSON body are short-lived (5 min) and must
  // travel as a header because the cookie scope is too narrow for the
  // /auth/2fa/verify roundtrip during the second-factor flow. Every
  // other authenticated request uses cookies + CSRF.
  if (options.token) {
    headers['Authorization'] = `Bearer ${options.token}`;
  }

  // Echo the CSRF cookie as a header on every request. Safe on GETs
  // (server ignores it on read paths). On state-changing requests
  // flask-jwt-extended verifies the header value against the matching
  // CSRF cookie — without this header the request is rejected with 401.
  const csrfAccess = readCsrfCookie('csrf_access_token');
  if (csrfAccess) {
    headers['X-CSRF-Token'] = csrfAccess;
  }

  // Merge caller-supplied headers (e.g. DPTA's `If-Match` optimistic-
  // concurrency version). Applied AFTER Authorization/CSRF — callers must
  // not clobber those — and BEFORE the body block so the FormData path can
  // still strip any manually-set Content-Type (the browser MUST set the
  // multipart boundary itself).
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  // credentials: 'include' tells fetch to send the JWT cookies on the
  // request. The browser only sends them when same-origin (or when CORS
  // explicitly allows credentials), so this doesn't broaden the cookie's
  // exposure beyond the existing same-origin contract.
  const fetchOptions = { method, headers, credentials: 'include' };
  if (data !== undefined && data !== null) {
    if (options.isFormData || data instanceof FormData) {
      // Let the browser set Content-Type with the correct boundary for
      // FormData — drop any caller-supplied Content-Type, which would omit
      // the boundary and corrupt the multipart body.
      delete headers['Content-Type'];
      fetchOptions.body = data;
    } else if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(data);
    } else {
      fetchOptions.body = JSON.stringify(data);
    }
  }

  let response;
  try {
    response = await fetch(fullUrl, fetchOptions);
  } catch (error) {
    const networkErr = new Error('Network Error \u2014 please check your connection');
    networkErr.status = 0;
    networkErr.details = null;
    throw networkErr;
  }

  // Handle 401 with token refresh (skip for explicit token overrides like pre-2FA).
  // Step-up auth 401s (`step_up_failed` / `step_up_required` / `user_inactive`)
  // are NOT JWT-expiry signals — the session is fine, the request-body
  // password failed. Refreshing + retrying here silently doubles every
  // failed password attempt against the 5-try lockout window in
  // security/step_up_auth.py, and clobbers the original error code so the
  // caller can't render the correct inline message.
  if (
    response.status === 401
    && !options._isRetry
    && !options.token
    && !(await isStepUp401(response))
  ) {
    try {
      await refreshWithMutex();
    } catch (refreshError) {
      clearAuth();
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      const authErr = new Error('Unauthorized');
      authErr.status = 401;
      authErr.details = null;
      throw authErr;
    }

    // After refresh, cookies (including a rotated CSRF cookie) are set
    // by Set-Cookie on the refresh response. Re-read the CSRF cookie so
    // the new value rides along the retry instead of the stale one.
    const newCsrf = readCsrfCookie('csrf_access_token');
    if (newCsrf) {
      headers['X-CSRF-Token'] = newCsrf;
    } else {
      delete headers['X-CSRF-Token'];
    }
    let retryResponse;
    try {
      retryResponse = await fetch(fullUrl, { ...fetchOptions, headers });
    } catch (error) {
      const retryNetErr = new Error('Network Error \u2014 please check your connection');
      retryNetErr.status = 0;
      retryNetErr.details = null;
      throw retryNetErr;
    }

    if (!retryResponse.ok) {
      throw await parseErrorResponse(retryResponse);
    }

    const retryData = options.responseType === 'blob'
      ? await retryResponse.blob().catch(() => null)
      : await retryResponse.json().catch(() => null);
    return { data: retryData, status: retryResponse.status, headers: headersToObject(retryResponse.headers) };
  }

  if (!response.ok) {
    const err = await parseErrorResponse(response);
    // ON-13 — Dispatch a global event when the backend signals LICENSE_READONLY
    // (403 + code === 'LICENSE_READONLY') so any mounted LicenseBanner can
    // react immediately without waiting for the 5-min poll cycle.
    // Mirror: auth:session-expired pattern above.
    if (response.status === 403 && err.code === 'LICENSE_READONLY') {
      window.dispatchEvent(new CustomEvent('license:read-only'));
    }
    throw err;
  }

  const responseData = options.responseType === 'blob'
    ? await response.blob().catch(() => null)
    : await response.json().catch(() => null);
  return { data: responseData, status: response.status, headers: headersToObject(response.headers) };
}

export const httpClient = {
  get: (url, options) => request('GET', url, undefined, options),
  post: (url, data, options) => request('POST', url, data, options),
  put: (url, data, options) => request('PUT', url, data, options),
  patch: (url, data, options) => request('PATCH', url, data, options),
  delete: (url, options) => {
    if (options?.data) {
      const { data, ...rest } = options;
      return request('DELETE', url, data, rest);
    }
    return request('DELETE', url, undefined, options);
  },
};

// Expose token helpers for AuthContext
export { getAccessToken, storeTokens, clearAuth, API_BASE };
